/**
 * RcmAdvisoryModal — fires INSIDE the Purchases form on Save.
 *
 * Given a freshly-saved purchase_bill.id, calls the backend compliance-check
 * endpoint, then shows:
 *   1. RCM verdict (Notif 13/2017 CT(R) / Sec 9(4) / Sec 5(3)) + rate + rationale
 *   2. Sec 17(5) blocked-credit lines (deterministic + AI classification)
 *
 * User Actions:
 *   - "Confirm RCM"      → POST /apply-rcm       (posts Output GST + ITC RCM voucher)
 *   - "Accept 17(5) reversal" → POST /apply-17-5-reversal (posts reversal JV)
 *   - "Not RCM / Skip"   → close, nothing posted (advisory stays cached on bill)
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  RefreshCw, CheckCircle2, XCircle, ShieldCheck, ShieldAlert, Sparkles,
} from "lucide-react";

export default function RcmAdvisoryModal({ billId, open, onOpenChange, onDone }) {
  const [advisory, setAdvisory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(null);   // 'rcm' | '17_5' | null

  useEffect(() => {
    if (!open || !billId) return;
    setAdvisory(null);
    setLoading(true);
    api.post(`/purchase/bills/${billId}/compliance-check`, {})
      .then((r) => setAdvisory(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Advisory failed"))
      .finally(() => setLoading(false));
  }, [open, billId]);

  const applyRcm = async () => {
    setApplying("rcm");
    try {
      const r = await api.post(`/purchase/bills/${billId}/apply-rcm`, {});
      toast.success(`RCM posted · GST total ₹${(r.data.gst_total).toLocaleString("en-IN")} · Voucher ${r.data.voucher_id.slice(0, 8)}`);
      onDone?.("rcm_applied");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "RCM apply failed");
    }
    setApplying(null);
  };

  const applyReversal = async () => {
    setApplying("17_5");
    try {
      const r = await api.post(`/purchase/bills/${billId}/apply-17-5-reversal`, {});
      toast.success(`17(5) reversal posted · ₹${(r.data.reversed_amount).toLocaleString("en-IN")} · Voucher ${r.data.voucher_id.slice(0, 8)}`);
      onDone?.("17_5_applied");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reversal failed");
    }
    setApplying(null);
  };

  const inr = (n) => "₹ " + Number(n || 0).toLocaleString("en-IN",
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="rcm-advisory-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-500" /> Compliance Advisory
          </DialogTitle>
          <DialogDescription>
            AI + rules check for RCM applicability (Notif 13/2017 CT(R) / Sec 9(4) / Sec 5(3))
            and Sec 17(5) blocked-credit exposure. Review + confirm below.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="text-center text-muted-foreground py-8" data-testid="advisory-loading">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" /> Running compliance check…
          </div>
        )}

        {advisory && (
          <div className="space-y-3">
            {/* ---------------- RCM block ---------------- */}
            <div className={`border-l-4 rounded-sm p-3 ${
              advisory.rcm.is_rcm ?
                "border-fuchsia-500 bg-fuchsia-500/10" :
                "border-emerald-500 bg-emerald-500/10"}`}
              data-testid="advisory-rcm-block">
              <div className="flex items-start gap-3">
                <ShieldCheck className={`w-5 h-5 ${advisory.rcm.is_rcm ? "text-fuchsia-500" : "text-emerald-500"}`} />
                <div className="flex-1">
                  <div className="text-sm font-bold">
                    {advisory.rcm.is_rcm ? "⚠ RCM APPLIES" : "✓ No RCM applicable"}
                  </div>
                  {advisory.rcm.is_rcm && (
                    <>
                      <div className="text-xs text-muted-foreground mt-1">{advisory.rcm.notification}</div>
                      <div className="text-xs mt-2 font-mono uppercase tracking-widest text-fuchsia-400">
                        Category: {advisory.rcm.category} · Rate {advisory.rcm.suggested_rate_pct}%
                      </div>
                      <div className="text-xs mt-1 text-white/80">{advisory.rcm.rationale}</div>
                      <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                        <div>Taxable: <span className="font-mono font-bold">{inr(advisory.rcm.rcm_taxable_value)}</span></div>
                        <div>Suggested GST: <span className="font-mono font-bold text-fuchsia-500">{inr(advisory.rcm.rcm_suggested_gst)}</span></div>
                        <div>Self-invoice: <span className="font-bold">{advisory.rcm.self_invoice_required ? "REQUIRED" : "Not needed"}</span></div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* ---------------- 17(5) block ---------------- */}
            <div className={`border-l-4 rounded-sm p-3 ${
              advisory.itc_17_5.verdict === "ineligible" ? "border-red-500 bg-red-500/10" :
              advisory.itc_17_5.verdict === "partial" ? "border-amber-500 bg-amber-500/10" :
              "border-emerald-500 bg-emerald-500/10"}`}
              data-testid="advisory-17-5-block">
              <div className="flex items-start gap-3">
                <ShieldAlert className={`w-5 h-5 ${
                  advisory.itc_17_5.verdict === "ineligible" ? "text-red-500" :
                  advisory.itc_17_5.verdict === "partial" ? "text-amber-500" :
                  "text-emerald-500"}`} />
                <div className="flex-1">
                  <div className="text-sm font-bold flex items-center gap-2">
                    Sec 17(5) verdict:
                    <span className="font-mono uppercase tracking-widest text-xs">
                      {advisory.itc_17_5.verdict}
                    </span>
                    {advisory.itc_17_5.ai_used && (
                      <span className="text-[9px] font-mono uppercase tracking-widest bg-indigo-500/20 text-indigo-400 px-1.5 py-0.5 rounded-sm">
                        AI verified
                      </span>
                    )}
                  </div>
                  {advisory.itc_17_5.verdict !== "eligible" && (
                    <>
                      <div className="text-xs mt-1">
                        Total ITC to reverse:
                        <span className="ml-1 font-mono font-bold text-red-500">{inr(advisory.itc_17_5.ineligible_amount)}</span>
                      </div>
                      <div className="mt-2 max-h-40 overflow-y-auto text-xs space-y-1">
                        {advisory.itc_17_5.lines
                          .filter((l) => l.ineligible)
                          .map((l, idx) => (
                            <div key={idx} className="border-l-2 border-white/10 pl-2">
                              <div className="font-mono text-amber-400">{l.clause} · {inr(l.ineligible_amount)}</div>
                              <div className="text-muted-foreground">{l.reason}</div>
                            </div>
                          ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}
            data-testid="advisory-skip">
            Skip for now
          </Button>
          {advisory?.rcm?.is_rcm && (
            <Button onClick={applyRcm} disabled={applying === "rcm"}
              className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white gap-1.5"
              data-testid="advisory-confirm-rcm">
              {applying === "rcm" ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Confirm RCM · Post ₹{Number(advisory?.rcm?.rcm_suggested_gst || 0).toLocaleString("en-IN")}
            </Button>
          )}
          {advisory?.itc_17_5?.verdict !== "eligible" && (
            <Button onClick={applyReversal} disabled={applying === "17_5"}
              className="bg-red-600 hover:bg-red-700 text-white gap-1.5"
              data-testid="advisory-accept-17-5">
              {applying === "17_5" ? <RefreshCw className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
              Accept 17(5) Reversal · ₹{Number(advisory?.itc_17_5?.ineligible_amount || 0).toLocaleString("en-IN")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
